import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import type { ProjectAgent } from '@/frontend/features/Fde/types'
import type { ProactivityCopy } from '@/frontend/features/Fde/ProjectAgentProactivity'

export default function MeetingPreparationControl({ projectId, assignment, copy, disabled }: {
  projectId: string; assignment: ProjectAgent; copy: ProactivityCopy; disabled: boolean
}) {
  const settings = assignment.proactivity!
  const initial = settings.meeting_preparation!
  const [draft, setDraft] = useState(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const save = (value: typeof initial) => {
    setBusy(true)
    setError('')
    router.patch(`/dashboard/discovery/agent_proactivities/${assignment.id}?project_id=${projectId}`, {
      revision: settings.revision, proactivity: { meeting_preparation: value },
    }, { preserveScroll: true, onError: (errors) => setError(errors.proactivity || ''), onFinish: () => setBusy(false) })
  }
  return <div className="space-y-3" aria-busy={busy}>
    <label className="flex items-start gap-2.5 text-sm">
      <Checkbox className="mt-0.5" checked={initial.enabled} disabled={disabled || busy}
        aria-label={`${assignment.agent.name}: ${copy.meeting_preparation}`}
        onCheckedChange={(value) => save({ ...initial, enabled: value === true })} />
      <span><span>{copy.meeting_preparation}</span><span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{copy.meeting_preparation_description}</span></span>
    </label>
    {initial.enabled && <div className="ml-6 max-w-sm space-y-1.5 text-xs">
      <label htmlFor={`meeting-delivery-${assignment.id}`} className="block font-medium text-foreground">{copy.meeting_delivery}</label>
      <select id={`meeting-delivery-${assignment.id}`} aria-label={`${assignment.agent.name}: ${copy.meeting_delivery}`} value={initial.delivery_mode}
        disabled={disabled || busy} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
        onChange={(event) => save({ ...initial, delivery_mode: event.target.value as typeof initial.delivery_mode })}>
        <option value="conversation">{copy.meeting_delivery_conversation}</option>
        <option value="native">{copy.meeting_delivery_native}</option>
      </select>
      <span className="block leading-relaxed text-muted-foreground">{initial.delivery_mode === 'conversation'
        ? copy.meeting_delivery_conversation_description : copy.meeting_delivery_native_description}</span>
    </div>}
    <details className="ml-6 text-xs text-muted-foreground">
      <summary className="w-fit cursor-pointer">{copy.meeting_schedule}</summary>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        {([
          ['business_hours', copy.meeting_hours, 1, 72],
          ['day_starts_at', copy.meeting_day_starts, 0, 23],
          ['day_ends_at', copy.meeting_day_ends, 1, 24],
        ] as const).map(([key, label, min, max]) => <label key={key} className="space-y-1">
          <span className="block">{label}</span>
          <input type="number" min={min} max={max} value={draft[key]} disabled={disabled || busy}
            className="h-8 w-24 rounded-md border border-input bg-background px-2 text-foreground"
            onChange={(event) => setDraft({ ...draft, [key]: Number(event.target.value) })} />
        </label>)}
        <Button type="button" size="sm" variant="outline" disabled={disabled || busy} onClick={() => save(draft)}>{copy.meeting_save}</Button>
      </div>
    </details>
    {error && <p role="alert" className="ml-6 text-xs text-destructive">{error}</p>}
  </div>
}
